import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapPin, Map, Trophy, Play, Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useGame } from '../contexts/GameContext';
import gameService from '../services/gameService';
import GameRound from '../components/game/GameRound';
import SplashScreen from '../components/game/SplashScreen';
import './GamePage.css';

const GamePage = () => {
  const [showSplash, setShowSplash] = useState(false);
  const [gameSession, setGameSession] = useState(null);
  const [currentRound, setCurrentRound] = useState(1);
  const [roundData, setRoundData] = useState(null);
  const [gameState, setGameState] = useState('init'); // init, playing
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const { user } = useAuth();
  const { setIsPlaying } = useGame();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user && gameState === 'init') {
      navigate('/login');
    }
  }, [user, gameState, navigate]);

  // Update game playing state for hiding nav/footer
  useEffect(() => {
    setIsPlaying(gameState === 'playing');
    return () => setIsPlaying(false); // Cleanup on unmount
  }, [gameState, setIsPlaying]);

  const startGame = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setShowSplash(true);
    setLoading(true);
    try {
      const data = await gameService.startGame();
      
      setGameSession(data.game_session);
      setCurrentRound(1);
      setGameState('playing');
      
      // Load first round
      await loadRound(data.game_session.id, 1);
    } catch (err) {
      console.error('Failed to start game:', err);
      alert('Failed to start game. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadRound = async (sessionId, roundNumber) => {
    setLoading(true);
    try {
      const data = await gameService.getRound(sessionId, roundNumber);
      setRoundData(data.round);
      setGameState('playing');
    } catch (err) {
      console.error('Failed to load round:', err);
      alert('Failed to load round. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const [roundResult, setRoundResult] = useState(null);

  const handleGuess = async (lat, lng) => {
    if (!gameSession || !roundData) return;
    
    setIsSubmitting(true);
    try {
      const data = await gameService.submitGuess(gameSession.id, currentRound, {
        guess_latitude: lat,
        guess_longitude: lng,
      });

      // Show result on map
      setRoundResult(data.result);
    } catch (err) {
      console.error('Failed to submit guess:', err);
      alert('Failed to submit guess. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueAfterResult = async () => {
    if (!gameSession) return;

    // Check if we've completed all rounds
    if (currentRound >= gameSession.total_rounds) {
      try {
        const sessionData = await gameService.getResults(gameSession.id);
        // Reset local state and navigate to the dedicated results route.
        setGameSession(null);
        setCurrentRound(1);
        setRoundData(null);
        setRoundResult(null);
        setGameState('init');
        navigate('/game/results', { state: { results: sessionData.results } });
      } catch (err) {
        console.error('Failed to load results:', err);
      }
    } else {
      // Load next round
      const nextRound = currentRound + 1;
      setCurrentRound(nextRound);
      setRoundResult(null);
      await loadRound(gameSession.id, nextRound);
    }
  };

  // Auto-start when navigated here from "Play Again".
  useEffect(() => {
    if (location.state?.autoStart && user && gameState === 'init') {
      // Clear state so refresh doesn't loop into another auto-start.
      navigate(location.pathname, { replace: true, state: {} });
      startGame();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, user]);

  if (!user) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className={`game-page${gameState === 'init' ? ' game-page--init' : ''}`}>
      {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
      {gameState === 'init' && (
        <div className="game-hero">
          <h1 className="game-hero-title">Map Hunt</h1>
          <p className="game-hero-sub">
            Explore Nepal through Street View and guess where you are on the map.
            5 rounds, scored on accuracy.
          </p>

          <div className="game-hero-features">
            <div className="game-feature">
              <Map size={14} />
              <span>5 Rounds</span>
            </div>
            <div className="game-feature">
              <MapPin size={14} />
              <span>Street View</span>
            </div>
            <div className="game-feature">
              <Trophy size={14} />
              <span>Leaderboard</span>
            </div>
          </div>

          <button
            className="game-start-btn"
            onClick={startGame}
            disabled={loading}
          >
            {loading
              ? <><Loader2 size={18} className="spin-icon" /> Starting…</>
              : <><Play size={18} /> Start Game</>
            }
          </button>
        </div>
      )}

      {gameState === 'playing' && roundData && (
        <GameRound
          roundNumber={currentRound}
          totalRounds={gameSession?.total_rounds || 5}
          dropLocation={roundData.drop_location}
          actualLocation={roundData.location}
          hints={roundData.hints || []}
          onGuess={handleGuess}
          isSubmitting={isSubmitting}
          result={roundResult}
          onContinue={handleContinueAfterResult}
        />
      )}

    </div>
  );
};

export default GamePage;
